import React, { useEffect } from 'react';
import '../styles/globals.css';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { ServiceCard } from "../components/ServiceCard";
import { AcneServiceCard } from "../components/AcneServiceCard";
import { TestimonialCard } from "../components/TestimonialCard";
import { HeroCarousel } from "../components/HeroCarousel";
import { ExpertCard } from "../components/ExpertCard";
import { BeforeAfterCard } from "../components/BeforeAfterCard";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import LogoImage from '../assets/images/logo.png';
import AcneConsultImage from '../assets/images/AcneConsult.jpg';
import NutritionConsultImage from '../assets/images/NutritionConsult.jpg';
import BundledConsultImage from '../assets/images/BundledConsult.PNG';
import NutritionistExpertImage from '../assets/images/NutritionistExpert.PNG';
import DermatologistExpertImage from '../assets/images/DermatologistExpert.JPG';
import {
  Stethoscope,
  FlaskConical,
  Salad,
  Instagram,
  MessageCircle,
  Menu,
  Users,
  Heart,
  Baby,
  Activity,
  Phone,
  Mail,
  MapPin,
  Clock,
  Award,
  Shield,
  TrendingUp,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { FaInstagram } from 'react-icons/fa';


const LandingPage = () => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const navigate = useNavigate();

  const carouselSlides = [
    {
      title: "Acne Consultation",
      subtitle: "Personalized Treatment Plans",
      description: "Transform your skin with a personalized acne solution",
      currentPrice: "Only ₹299",
      oldPrice: "₹499",
      categories: ["Hormonal Acne", "Aging Acne", "Teenage Acne"],
      image: AcneConsultImage,
    },
    {
      title: "Nutritionist Consultation",
      subtitle: "Acne Recovery from Within",
      description: "Heal acne from within with a custom 7-day nutrition plan",
      currentPrice: "Only ₹299",
      oldPrice: "₹499",
      categories: ["Personalized Diet Plans", "Supplement Guidance", "Ongoing Support"],
      image: NutritionConsultImage,
    },
    {
      title: "Acne + Nutrition Package",
      subtitle: "The Complete Solution",
      description: "Get clear, healthy skin inside and out with our bundled and comprehensive care",
      currentPrice: "Only ₹499",
      oldPrice: "₹599",
      categories: ["Expert Consultation", "Custom Nutrition Plan", "Holistic Approach"],
      image: BundledConsultImage,
    },
  ];

  const testimonials = [
    {
      name: "Janhavi Deshmukh, Age 27 ",
      quote: "Had a really great experience with the dermatologist! I suddenly had swollen lips and flaky skin, but she quickly identified the issue and prescribed the perfect medication. My skin was back to normal within a week.",
      rating: 5,
    },
    {
      name: "Prachi Parashar, Age 27",
      quote: "I had the best experience with Dr. Aishwarya regarding my face acne. She listened patiently to my concerns, explained my condition in detail, and created a personalized treatment plan. Thanks to her expertise and care, my skin improved significantly and I feel much more confident now. Currently I am using her advised care routine and the results are satisfactory.",
      rating: 5,
    },
    {
      name: "Shipra Nalamwar, Age 32",
      quote: "My experience with the doctor was excellent. She was very patient, attentive, and took the time to explain everything clearly. She effectively addressed my acne concerns, and I am very happy with the results.",
      rating: 5,
    },
  ];

  const experts = [
    {
      name: "Dr. Aishwarya Dube",
      title: "Dermatologist",
      credentials: "MBBS, DDV",
      specialties: ["Hormonal Acne", "Adult Acne", "Skin Health"],
      image: DermatologistExpertImage,
      bio: "With experience of treating 20,000+ patients, Dr. Dube specializes in treating complex acne cases, eczema, skin allergies, infections and autoimmune skin disorders.",
    },
    {
      name: "Dr. Radhika Deo",
      title: "Nutritionist",
      credentials: "BDS, PGD",
      specialties: ["Acne Nutrition", "Anti-Inflammatory Diet", "Gut Health"],
      image: NutritionistExpertImage,
      bio: "Dr. Deo helps clients achieve clear skin through personalized nutrition plans, focusing on the connection between diet, inflammation, and skin health.",
    },
  ];

  const acneServices = [
    {
      icon: Users,
      title: "Teenage Acne",
      description: "Specialized treatment for adolescent acne addressing hormonal changes and skin development.",
      features: [
        "Age-appropriate skincare routines",
        "Hormonal balance management",
        "Scar prevention strategies",
        "Educational support for teens",
      ],
    },
    {
      icon: Heart,
      title: "Adult Acne",
      description: "Comprehensive care for adult-onset acne with focus on lifestyle and stress factors.",
      features: [
        "Stress-related acne treatment",
        "Anti-aging + acne solutions",
        "Professional skincare protocols",
        "Long-term maintenance plans",
      ],
    },
    {
      icon: Activity,
      title: "PCOS Acne",
      description: "Targeted treatment for PCOS-related acne addressing hormonal imbalances.",
      features: [
        "Hormonal testing & analysis",
        "PCOS-specific treatments",
        "Nutritional support plans",
        "Ongoing monitoring",
      ],
    },
    {
      icon: Baby,
      title: "Pregnancy Acne",
      description: "Safe and gentle acne treatments designed specifically for expecting mothers.",
      features: [
        "Pregnancy-safe treatments",
        "Natural skincare options",
        "Hormonal shift management",
        "Postpartum care guidance",
      ],
    },
  ];

  const stats = [
    { icon: Users, value: "20,000+", label: "Skin Consultations" },
    { icon: Shield, value: "100%", label: "Transparent pricing" },
    { icon: Clock, value: "5+", label: "Years Experience" },
    { icon: MessageCircle, value: "7 days", label: "Support post consultation" },
  ];

  const beforeAfterResults = [
    {
      beforeImage: "https://images.unsplash.com/photo-1619002117199-47c7f0427d21?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB3b21hbiUyMHNraW5jYXJlJTIwYmVhdXR5fGVufDF8fHx8MTc2MTM2NzQ5M3ww&ixlib=rb-4.1.0&q=80&w=1080",
      afterImage: "https://images.unsplash.com/photo-1734764627104-6ad22c48af6a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB3b21hbiUyMGJlYXV0aWZ1bCUyMHBvcnRyYWl0fGVufDF8fHx8MTc2MTMwNjg5M3ww&ixlib=rb-4.1.0&q=80&w=1080",
      clientName: "Kavya M.",
      treatment: "PCOS Acne Treatment",
      duration: "12 weeks",
      results: "95% reduction in breakouts, balanced hormones, improved confidence",
    },
    {
      beforeImage: "https://images.unsplash.com/photo-1704927768421-bc9549b5097d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB3b21hbiUyMHNraW5jYXJlJTIwY29uc3VsdGF0aW9ufGVufDF8fHx8MTc2MTM2NzQ4OXww&ixlib=rb-4.1.0&q=80&w=1080",
      afterImage: "https://images.unsplash.com/photo-1734112727935-b1e79156e4b9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB3b21hbiUyMGZhY2UlMjBjbG9zZXVwfGVufDF8fHx8MTc2MTM2NzQ5M3ww&ixlib=rb-4.1.0&q=80&w=1080",
      clientName: "Aisha K.",
      treatment: "Adult Acne + Nutrition",
      duration: "16 weeks",
      results: "Clear skin achieved, reduced inflammation, sustainable lifestyle changes",
    },
    {
      beforeImage: "https://images.unsplash.com/photo-1693382505795-3285ef9370be?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBudXRyaXRpb25pc3QlMjB3b21hbnxlbnwxfHx8fDE3NjEzNjc0OTB8MA&ixlib=rb-4.1.0&q=80&w=1080",
      afterImage: "https://images.unsplash.com/photo-1693362029321-eb86dc114bc4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjB3b21hbiUyMGNsZWFyJTIwc2tpbnxlbnwxfHx8fDE3NjEzNjc0OTF8MA&ixlib=rb-4.1.0&q=80&w=1080",
      clientName: "Diya R.",
      treatment: "Teenage Acne Program",
      duration: "8 weeks",
      results: "Dramatic improvement in skin texture, boosted self-esteem, maintained results",
    },
  ];

  const handleBookNow = () => {
    const phoneNumber = '918788631381';
    const message = 'Hello! I would like to take acne consultation with you.';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleUltimatePackageBookNow = () => {
    const phoneNumber = '918788631381';
    const message = 'Hello! I would like to take acne + nutrition package consultation with you.';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleBookENT = () => {
  const phoneNumber = '917887878109';
  const message = 'Hello! I would like to book an ENT consultation with Dr. Sourabh Kosalge.';
  const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank');
  };

  const handleSkinTest = () => {
    navigate('/skin-quiz'); // replace with your route
  };

  const scrollToSection = (sectionId: string) => {
    setSheetOpen(false);
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-[#d4a574] to-[#c89464] text-white py-2 px-6">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center md:justify-between gap-4 text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+918788631381" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Phone className="w-4 h-4" />
              <span>+91 8788631381</span>
            </a>
            <a href="mailto:info@clearandbare.com" className="hidden sm:flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Mail className="w-4 h-4" />
              <span>clearandbare.skincare@gmail.com</span>
            </a>
          </div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <span>Book your consultation today</span>
            <span>Starting at only ₹299 <del>₹499</del></span>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-[#d4a574] to-[#c89464] rounded-full flex items-center justify-center">
                <img src={LogoImage} alt="Clear&Bare" />
                {/* <Sparkles className="w-5 h-5 text-white" /> */}
              </div>
              <div>
                <h1 className="text-[#2c2c2c] tracking-tight leading-none">Clear & Bare</h1>
                <p className="text-xs text-[#999]">Skincare Simplified</p>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <a href="#home" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Home
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#experts" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Our Experts
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="#services" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Services
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300"></span>
              </a>
              {/* <a href="#results" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Results
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300"></span>
              </a> */}
              <a href="#testimonials" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Testimonials
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300"></span>
              </a>
              <a href="/ent" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                ENT Care ✦
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300" />
              </a>
              <a href="#contact" className="text-[#666] hover:text-[#d4a574] transition-colors relative group">
                Contact
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#d4a574] group-hover:w-full transition-all duration-300"></span>
              </a>
            </nav>

            {/* CTA Button - Desktop */}
            <div className="hidden lg:flex items-center gap-4">
              <Button
                onClick={handleBookNow}
                className="bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white rounded-full px-6 shadow-lg shadow-[#d4a574]/30 transition-all duration-300"
              >
                <span className="flex flex-col items-start">
                    <span>Book Consultation</span>
                </span>             
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Mobile Menu Drawer */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-6 h-6 text-[#2c2c2c]" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-white">
                <SheetHeader>
                  <SheetTitle className="text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#d4a574] to-[#c89464] rounded-full flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-[#2c2c2c]">Clear & Bare</div>
                        <div className="text-xs text-[#999]">Skincare Excellence</div>
                      </div>
                    </div>
                  </SheetTitle>
                </SheetHeader>
                
                <Separator className="my-6" />
                
                <nav className="flex flex-col gap-1">
                  <button
                    onClick={() => scrollToSection("home")}
                    className="flex items-center justify-between px-4 py-3 text-[#666] hover:bg-[#faf8f5] hover:text-[#d4a574] rounded-lg transition-all group"
                  >
                    <span>Home</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <button
                    onClick={() => scrollToSection("services")}
                    className="flex items-center justify-between px-4 py-3 text-[#666] hover:bg-[#faf8f5] hover:text-[#d4a574] rounded-lg transition-all group"
                  >
                    <span>Services</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <button
                    onClick={() => navigate('/ent')}
                    className="flex items-center justify-between px-4 py-3 text-[#666] hover:bg-[#faf8f5] hover:text-[#d4a574] rounded-lg transition-all group"
                  >
                    <span>ENT Care ✦</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  {/* <button
                    onClick={() => scrollToSection("results")}
                    className="flex items-center justify-between px-4 py-3 text-[#666] hover:bg-[#faf8f5] hover:text-[#d4a574] rounded-lg transition-all group"
                  >
                    <span>Results</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button> */}
                  <button
                    onClick={() => scrollToSection("experts")}
                    className="flex items-center justify-between px-4 py-3 text-[#666] hover:bg-[#faf8f5] hover:text-[#d4a574] rounded-lg transition-all group"
                  >
                    <span>Our Experts</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <button
                    onClick={() => scrollToSection("testimonials")}
                    className="flex items-center justify-between px-4 py-3 text-[#666] hover:bg-[#faf8f5] hover:text-[#d4a574] rounded-lg transition-all group"
                  >
                    <span>Testimonials</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <button
                    onClick={() => scrollToSection("contact")}
                    className="flex items-center justify-between px-4 py-3 text-[#666] hover:bg-[#faf8f5] hover:text-[#d4a574] rounded-lg transition-all group"
                  >
                    <span>Contact</span>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </nav>

                <Separator className="my-6" />

                <div className="space-y-4">
                  <Button
                    onClick={() => {
                      setSheetOpen(false);
                      handleBookNow();
                    }}
                    className="w-full bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white rounded-full shadow-lg shadow-[#d4a574]/30"
                  >
                    Book Consultation
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>                

                  <div className="space-y-3 pt-4">
                    <a href="tel:+918788631381" className="flex items-center gap-3 text-[#666] hover:text-[#d4a574] transition-colors">
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">+1 (234) 567-890</span>
                    </a>
                    <a href="mailto:info@clearandbare.com" className="flex items-center gap-3 text-[#666] hover:text-[#d4a574] transition-colors">
                      <Mail className="w-4 h-4" />
                      <span className="text-sm">info@clearandbare.com</span>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero Section with Carousel */}
      <section id="home" className="relative">
        <HeroCarousel slides={carouselSlides} onBookNow={handleBookNow} />
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-[#faf8f5] to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNkNGE1NzQiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDE2YzAtNC40MTggMy41ODItOCA4LThzOCAzLjU4MiA4IDgtMy41ODIgOC04IDgtOC0zLjU4Mi04LTh6bS0xNiAwYzAtNC40MTggMy41ODItOCA4LThzOCAzLjU4MiA4IDgtMy41ODIgOC04IDgtOC0zLjU4Mi04LTh6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-lg shadow-[#d4a574]/10 mb-4 group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="w-8 h-8 text-[#d4a574]" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-[#2c2c2c] mb-1">{stat.value}</div>
                <div className="text-[#666] text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Experts Section */}
      <section id="experts" className="py-20 bg-gradient-to-br from-[#faf8f5] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-white text-[#d4a574] border border-[#d4a574]/20 px-4 py-1">
              Our Team
            </Badge>
            <h2 className="text-4xl md:text-5xl text-[#2c2c2c] mb-4">Meet Our Experts</h2>
            <p className="text-[#666] max-w-2xl mx-auto text-lg">
              Our dermatologist and nutritionist are dedicated 
              to helping you achieve clear, radiant skin
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {experts.map((expert, index) => (
              <ExpertCard
                key={index}
                name={expert.name}
                title={expert.title}
                credentials={expert.isNew && (
                 <Badge className="mt-2 bg-gradient-to-r from-[#d4a574] to-[#c89464] text-white border-0 text-xs px-3 py-0.5">
                  New
                 </Badge>
                )}
                specialties={expert.specialties}
                image={expert.image}
                bio={expert.bio}
              />
            ))}
          </div>
        </div>
      </section>


      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-[#f9f1e8] text-[#d4a574] border-0 px-4 py-1">
              Our Services
            </Badge>
            <h2 className="text-4xl md:text-5xl text-[#2c2c2c] mb-4">Comprehensive Skincare Solutions</h2>
            <p className="text-[#666] max-w-2xl mx-auto text-lg">
              Tailored treatments designed to address your unique skin needs with precision and care
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <ServiceCard
              icon={Stethoscope}
              title="Acne Consultation"
              description="Expert dermatological assessment and personalized treatment plans for all types of acne, from hormonal to teenage breakouts at just ₹299."
              buttonText='Book Now'
              onClick={handleBookNow}
            />
            <ServiceCard
              icon={FlaskConical}
              title="Free Skin Test"
              description="Advanced diagnostic testing to identify your skin type, sensitivities, and underlying causes of skin concerns."
              buttonText='Take Free Quiz'
              onClick={handleSkinTest}
            />
            <ServiceCard
              icon={Salad}
              title="Nutrition Consultancy"
              description="Personalized dietary guidance to support skin health from within, addressing the root causes of acne through nutrition at just ₹299."
              buttonText='Book Now'
              onClick={handleBookNow}              
            />
          </div>
        </div>
      </section>

      {/* Acne Services Section */}
      <section className="py-20 bg-gradient-to-br from-[#faf8f5] to-[#f9f1e8]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-white text-[#d4a574] border border-[#d4a574]/20 px-4 py-1">
              Specialized Treatments
            </Badge>
            <h2 className="text-4xl md:text-5xl text-[#2c2c2c] mb-4">Acne Care for Every Life Stage</h2>
            <p className="text-[#666] max-w-2xl mx-auto text-lg">
              We understand that every acne journey is unique. Our specialized treatments are 
              designed for your specific life stage and skin needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {acneServices.map((service, index) => (
              <AcneServiceCard
                key={index}
                icon={service.icon}
                title={service.title}
                description={service.description}
                features={service.features}
                onBookNow={handleBookNow}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Before & After Results Section
      <section id="results" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-[#f9f1e8] text-[#d4a574] border-0 px-4 py-1">
              Real Results
            </Badge>
            <h2 className="text-4xl md:text-5xl text-[#2c2c2c] mb-4">
              Transformations That Speak for Themselves
            </h2>
            <p className="text-[#666] max-w-2xl mx-auto text-lg">
              See the remarkable before and after results our clients have achieved through personalized 
              acne treatment and nutrition plans
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {beforeAfterResults.map((result, index) => (
              <BeforeAfterCard
                key={index}
                beforeImage={result.beforeImage}
                afterImage={result.afterImage}
                clientName={result.clientName}
                treatment={result.treatment}
                duration={result.duration}
                results={result.results}
              />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-[#666] mb-6">
              Ready to start your transformation journey?
            </p>
            <Button
              onClick={handleBookNow}
              className="bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white rounded-full px-8 py-6 text-lg shadow-xl shadow-[#d4a574]/30 transition-all duration-300 group"
            >
              Book Your Consultation
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section> */}

      {/* Ultimate Package Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#d4a574]/5 rounded-full blur-3xl -z-0"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#c89464]/5 rounded-full blur-3xl -z-0"></div>
        
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#faf8f5] to-white rounded-3xl p-8 md:p-12 shadow-2xl shadow-[#d4a574]/10 border border-gray-100"
          >
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-1">
                <Badge className="mb-4 bg-gradient-to-r from-[#d4a574] to-[#c89464] text-white border-0 px-4 py-1">
                  Most Popular
                </Badge>
                <h2 className="text-3xl md:text-4xl text-[#2c2c2c] mb-3">
                  The Ultimate Clear Skin Plan
                </h2>
                <p className="text-[#d4a574] mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  <span>Consultation + Nutrition Package</span>
                </p>
                <p className="text-[#666] leading-relaxed mb-6">
                  Our comprehensive package combines expert dermatological consultation with personalized 
                  nutritional guidance for a holistic approach to achieving clear, healthy skin.
                </p>
                <p className="text-[#666] leading-relaxed mb-6">
                  At only ₹499 <del>₹599</del>
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-start gap-3 bg-white p-4 rounded-xl">
                    <Shield className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[#2c2c2c] mb-1">Holistic Approach</div>
                      <div className="text-[#999] text-sm">Address skin health from within</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-4 rounded-xl">
                    <Award className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[#2c2c2c] mb-1">Expert Guidance</div>
                      <div className="text-[#999] text-sm">Board-certified professionals</div>
                    </div>
                  </div>
                  {/* <div className="flex items-start gap-3 bg-white p-4 rounded-xl">
                    <Star className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[#2c2c2c] mb-1">Long-lasting Results</div>
                      <div className="text-[#999] text-sm">Sustainable skin health</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-white p-4 rounded-xl">
                    <Sparkles className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[#2c2c2c] mb-1">Save 20%</div>
                      <div className="text-[#999] text-sm">vs. separate bookings</div>
                    </div>
                  </div> */}
                </div>

                <Button
                  onClick={handleBookNow}
                  className="bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white rounded-full px-10 py-6 text-lg shadow-xl shadow-[#d4a574]/30 transition-all duration-300 group"
                >
                  Book Ultimate Package
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-[#faf8f5] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="mb-4 bg-[#f9f1e8] text-[#d4a574] border-0 px-4 py-1">
              Testimonials
            </Badge>
            <h2 className="text-4xl md:text-5xl text-[#2c2c2c] mb-4">What Our Clients Say</h2>
            <p className="text-[#666] max-w-2xl mx-auto text-lg">
              Real stories from people who achieved clear, healthy skin
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                name={testimonial.name}
                quote={testimonial.quote}
                rating={testimonial.rating}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#d4a574] to-[#c89464] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtNC40MTggMy41ODItOCA4LThzOCAzLjU4MiA4IDgtMy41ODIgOC04IDgtOC0zLjU4Mi04LTh6bS0xNiAwYzAtNC40MTggMy41ODItOCA4LThzOCAzLjU4MiA4IDgtMy41ODIgOC04IDgtOC0zLjU4Mi04LTh6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl text-white mb-6">
              Ready to Start Your Clear Skin Journey?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied clients who have transformed their skin with Clear & Bare. 
              Our experts are ready to create a customized plan just for you.
            </p>
            <Button
              onClick={handleBookNow}
              className="bg-white text-[#d4a574] hover:bg-gray-50 rounded-full px-10 py-6 text-lg shadow-2xl transition-all duration-300 group"
            >
              Schedule Your Consultation
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#2c2c2c] text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#d4a574] to-[#c89464] rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white">Clear & Bare</h3>
                  <p className="text-xs text-gray-400">Skincare Excellence</p>
                </div>
              </div> 
              <p className="text-gray-400 leading-relaxed mb-6">
                Your trusted partner in achieving clear, healthy skin through expert consultation 
                and personalized care. We combine cutting-edge dermatology with holistic nutrition 
                for transformative results.
              </p>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/clearandbare_2025"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow us on Instagram"
                  className="w-10 h-10 bg-white/10 hover:bg-[#d4a574] rounded-full flex items-center justify-center transition-colors"
                >
                    <FaInstagram className="w-5 h-5" />
                </a>
                <a
                  href="https://wa.me/918788631381"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 hover:bg-[#d4a574] rounded-full flex items-center justify-center transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#home" className="text-gray-400 hover:text-[#d4a574] transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Home
                  </a>
                </li>
                <li>
                  <a href="#services" className="text-gray-400 hover:text-[#d4a574] transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Services
                  </a>
                </li>
                {/* <li>
                  <a href="#results" className="text-gray-400 hover:text-[#d4a574] transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Results
                  </a>
                </li> */}
                <li>
                  <a href="#experts" className="text-gray-400 hover:text-[#d4a574] transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Our Experts
                  </a>
                </li>
                <li>
                  <a href="/ent" className="text-gray-400 hover:text-[#d4a574] transition-colors flex items-center gap-2 group">
                     <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                     ENT Care
                  </a>
                </li>
                <li>
                  <a href="#testimonials" className="text-gray-400 hover:text-[#d4a574] transition-colors flex items-center gap-2 group">
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    Testimonials
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-gray-400">
                  <Phone className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                  <a href="tel:+918788631381" className="hover:text-[#d4a574] transition-colors">
                    +91 8788631381
                  </a>
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <Mail className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                  <a href="mailto:info@clearandbare.com" className="hover:text-[#d4a574] transition-colors">
                    clearandbare.skincare@gmail.com
                  </a>
                </li>
                {/* <li className="flex items-start gap-3 text-gray-400">
                  <MapPin className="w-5 h-5 text-[#d4a574] mt-0.5 flex-shrink-0" />
                  <span>123 Skincare Avenue, Beauty District, CA 90210</span>
                </li> */}
              </ul>
            </div>
          </div>

          <Separator className="bg-white/10 mb-8" />

          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>© 2024 Clear & Bare. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="/legal" className="hover:text-[#d4a574] transition-colors">Privacy Policy</a>
              <a href="/legal" className="hover:text-[#d4a574] transition-colors">Terms of Service</a>
              <a href="/legal" className="hover:text-[#d4a574] transition-colors">Cancellation & Refunds</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating Action Button */}
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          className="fixed bottom-8 right-8 z-40"
        >
          <Button
            onClick={handleBookNow}
            className="w-16 h-16 rounded-full bg-gradient-to-r from-[#d4a574] to-[#c89464] hover:from-[#c89464] hover:to-[#b8845a] text-white shadow-2xl shadow-[#d4a574]/50 hover:scale-110 transition-all duration-300"
          >
            <MessageCircle className="w-6 h-6" />
          </Button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default LandingPage;